import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { DraggableSyntheticListeners } from '@dnd-kit/core';
import { X } from 'lucide-react';
import { ReactNode } from 'react';
import { useIntl } from 'react-intl';
import IconTile from '~/components/IconTile';

interface PanelHeaderProps {
    title: string;
    /** The panel's icon, shown in a tinted tile before the title. */
    icon?: ReactNode;
    dragListeners: DraggableSyntheticListeners;
    onClose: () => void;
    closeDisabled?: boolean;
    /** Draw the rule under the header; off when the host draws its own. */
    divider?: boolean;
}

/**
 * Header of a draggable panel: the title block is the drag handle, the
 * close control stays outside it so a press there never arms a drag.
 */
export default function PanelHeader({
    title,
    icon,
    dragListeners,
    onClose,
    closeDisabled = false,
    divider = true,
}: PanelHeaderProps) {
    const intl = useIntl();

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                ...(divider && {
                    pb: 1,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                }),
            }}
        >
            <Stack
                direction="row"
                alignItems="center"
                gap={1.5}
                flex={1}
                minWidth={0}
                {...dragListeners}
                sx={{
                    cursor: 'grab',
                    '&:active': { cursor: 'grabbing' },
                    userSelect: 'none',
                    touchAction: 'none',
                }}
                title={intl.formatMessage({ id: 'panel.dragToMove' })}
            >
                {icon && <IconTile>{icon}</IconTile>}
                <Typography
                    fontSize={15}
                    fontWeight={400}
                    noWrap
                    data-testid="panel-header-title"
                >
                    {title}
                </Typography>
            </Stack>
            <Tooltip
                title={intl.formatMessage({ id: 'close' })}
                placement="left"
            >
                <span style={{ flexShrink: 0 }}>
                    <IconButton
                        size="small"
                        onClick={onClose}
                        disabled={closeDisabled}
                        sx={{ p: 0.75 }}
                        aria-label={intl.formatMessage({ id: 'close' })}
                    >
                        <X size={16} />
                    </IconButton>
                </span>
            </Tooltip>
        </Box>
    );
}
